<script setup lang="ts">

import * as d3 from 'd3';
import { ref, watch } from 'vue';

import { ColoredSeries, TimeSeriesS } from '../fin/history';
import { setContextMenuHandler } from './context_menu';

interface Props {
  title: string;
  maxYear: number;
  birthYear: number;
  retirementYear: number;
  seriess: Array<ColoredSeries<number>>;
  seriesRenderer: (
    svg: d3.Selection<SVGSVGElement, undefined, null, undefined>,
    x: (x:number)=>number,
    y: (y:number)=>number,
  ) => void;
}

// "Reactive Props Destructure" to initialise props:
const {
  title = 'TODO: title',
  maxYear = -999,
  birthYear = 0,
  retirementYear = 0,
  seriess = [],
  seriesRenderer = (_svg, _x, _y) => {},
} = defineProps<Props>();

const root = ref<HTMLElement>();

watch(
  ()=>seriess,
  (newSeriess, _old) => {
    upsertChart(newSeriess, maxYear);
  }
);

function upsertChart(cseriess: Array<ColoredSeries<number>>, maxYear: number) {

  // Declare the chart dimensions and margins.
  const width = 1080;
  const height = 520;
  const marginTop = 40;
  const marginRight = 20;
  const marginBottom = 50;
  const marginLeft = 75;

  const years = cseriess.map(s => s.data.map(d => d.year)).flat();
  const firstYear = Math.min(...years, maxYear);
  const endYear = Math.max(...years);  // typically when money runs out, but not more than mazYear
  const lastYear = Math.max(endYear, maxYear);  // right hand extend of plot

  const values = cseriess.map(s => s.data.map(d => d.value)).flat();
  const minValue = Math.min(...values, 0);
  const maxValue = Math.max(...values, 0);

  const x = d3.scaleLinear()
              .domain([firstYear, lastYear])
              .range([marginLeft, width - marginRight]);

  const xAge = d3.scaleLinear()
                 .domain([firstYear - birthYear, lastYear - birthYear])
                 .range([marginLeft, width - marginRight]);

  // Declare the y (vertical position) scale.
  const y = d3.scaleLinear()
              .domain([minValue, maxValue])
              .range([height - marginBottom, marginTop]);

  // Create the SVG container.
  const svg = d3.create('svg')
                .attr('width', width)
                .attr('height', height)
                .attr('viewBox', [0, 0, width, height])
                .attr('style', 'max-width: 100%; height: auto;');

  // retirement background
  for( const area of [
    {
      start: firstYear,
      end: retirementYear,
      color: 'rgb(230, 255, 230)',
    }, {
      start: retirementYear,
      end: endYear,
      color: 'rgb(255, 255, 230)',
    }, {
      start: endYear,
      end: maxYear,
      color: 'rgb(255, 240, 240)',
    },
  ]) {
    svg.append('rect')
      .classed('post-retirement', true)
      .attr('x', x(area.start))
      .attr('y', 0)
      .attr('width', Math.max(0, x(area.end) - x(area.start)))
      .attr('height', height)
      .attr('fill', area.color);
  }

  const legend = svg.append('g')
    .classed('legend', true)
    .attr('transform', 'translate(' + (width - 325) + ', 10)');

  setContextMenuHandler(
    legend.node() as unknown as HTMLElement,
    new TimeSeriesS<number>(cseriess),
  );

  // title
  svg.append('text')
     .text(title)
     .style("font-size", "34px")
     .classed('title-text', true)
     .attr('alignment-baseline', 'top')
     .attr('text-anchor', 'start')
     .attr('x', 300)
     .attr('y', 25)
     .append('title')
     .text('saving pool sizes per year');

  // Add the x-axis.
  svg.append('g')
     .attr('transform', `translate(0,${height - marginBottom})`)
     .call(d3.axisBottom(x)
             .ticks(width / 80)
             .tickSizeOuter(0)
             .tickFormat(d3.format("d")));

  // Add the xAge-axis.
  svg.append('g')
     .attr('transform', `translate(0,${height - marginBottom + 25})`)
     .call(d3.axisBottom(xAge)
             .tickSizeOuter(5)
             .tickFormat(d3.format("d")));

  // Add the y-axis and label, and remove the domain line.
  svg.append('g')
     .attr('transform', `translate(${marginLeft},0)`)
     .call(d3.axisLeft(y).ticks(height / 40))
     // .call((g) => g.select('.domain').remove())
     .call(g => g.append('text')
     .attr('x', -marginLeft)
     .attr('y', 15)
     .attr('fill', 'currentColor')
     .attr('text-anchor', 'start')
     .text('↑ GBP'));
  
  const names = cseriess.map(cs => cs.name).sort();

  for (const [index, name] of Array.from(names.entries())) {
    const cs: ColoredSeries<number> = cseriess.filter(cs => cs.name==name)[0];

    legend
      .append('rect')
      .classed('legend-bar', true)
      .attr('series-visibility', 'visible')
      .attr('style', 'stroke-width:2;stroke:black;')
      .attr('x', 0)
      .attr('y', index * 18)
      .attr('width', 40)
      .attr('height', 12)
      .attr('fill', cs.color)
      .attr('default-fill', cs.color)
      .attr('series-visible', 'visible')
      .on('click', (evt: MouseEvent) => {
        // toggle 'visible'
        const swatch = evt.srcElement as SVGElement;
        toggleSeries(swatch);
      });

    legend
      .append('text')
      .classed('legend-text', true)
      .text(cs.name)
      .attr('x', 50)
      .attr('y', 12 + index * 18);
  }

  legend
    .append('text')
    .text('toggle all')
    .attr('text-anchor', 'start')
    .attr('alignment-baseline', 'hanging')
    .attr('x', 0)
    .attr('y', 10 + names.length * 18)
    .classed('fin-series-bulk-selection-btn', true)
    .on('click', (_evt: MouseEvent) => {
      Array.from(legend.selectChildren('.legend-bar')).forEach(
        elem => {
          toggleSeries(elem as SVGElement);
        }
      );
    })
    .append('title')
    .text('toggle all series visibility')
    ;

  function toggleSeries(swatchElem: SVGElement) {
    const colour: string = swatchElem.getAttribute('default-fill')!;
    const existingVisibility: string = swatchElem.getAttribute('series-visibility')!;
    const existingOn: boolean = existingVisibility == 'visible';
    const newOn = !existingOn;
    const newVisibility = newOn ? 'visible': 'hidden';
    swatchElem.setAttribute('fill', newOn ? colour : 'rgb(220,220,220)');
    swatchElem.setAttribute('series-visibility', newVisibility);
    const words = swatchElem.nextElementSibling!;
    words.setAttribute('fill', newOn ? 'black' : 'silver');
    const seriesElems: Element[] = Array.from( 
      document.getElementsByClassName('fin-series')
    ).filter(
      el => (el.getAttribute('stroke') ?? el.getAttribute('fill')) == colour
    );
    seriesElems.forEach(el => el.setAttribute('visibility', newVisibility));
  }

  seriesRenderer(svg, x, y);

  for( const child of Array.from(root!.value!.children) ) {
    root?.value?.removeChild(child);   
  }

  root?.value?.appendChild(svg.node()!);
}

</script>

<template>
  <div
    ref="root"
    class="fin-plot fin-plot:money-time output-card"
  />
</template>

<style scoped>
</style>
